import { useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import type { ConversationalFormStep } from '@karehero/models';
import Loading from '@karehero/llama/components/pages/Loading/Loading';
import { ConversationalFormPage } from '@karehero/llama/pwa/pages/ConversationalFormPage/ConversationalFormPage';
import type { RecorderProps } from '@karehero/llama/pwa/pages/ConversationalFormPage/ChatComposer';
import type { UploadSource } from '@karehero/llama/pwa/pages/ConversationalFormPage/UploadSheet';
import { ReadingLine } from '@karehero/llama/pwa/pages/ConversationalFormPage/ReadingLine';
import {
  ACCEPTED_DOCUMENT_TYPES_INPUT,
  ACCEPTED_IMAGE_TYPES,
} from '@karehero/helpers/files';

import { ROUTES } from 'router/routes';
import { useListConversationConfigsQuery } from 'store/api/chatSession';
import { useDocumentUrl } from 'hooks/useDocumentUrl';
import { useGoBack } from 'hooks/useGoBack';
import { useOpenUrl } from 'hooks/useOpenUrl';
import { useServerChat } from 'hooks/useServerChat';
import { useVoiceNoteDraft } from 'hooks/useVoiceNoteDraft';
import type { ServerQuestion } from 'utils/serverChatQuestions';

/** Said in turn while something sent, such as a file, is read. */
const READING_LINES = ["Going through what you've shared", 'Taking it all in'];

/** The chat stays open to anything typed, said or sent, a question being asked or not. */
const OPEN_STEP: ConversationalFormStep = {
  id: 'server-chat-open',
  presentation: 'chat',
  mode: 'expand',
  ask: '',
  placeholder: 'Type or say anything',
  isVoiceEnabled: true,
  isFileEnabled: true,
};

/**
 * The step answering the question being asked. One option is picked from the
 * question's own rows, with the chat still open; several are picked in rows of
 * their own; and a question without options is answered in the chat.
 */
const questionStep = (question?: ServerQuestion): ConversationalFormStep => {
  if (!question) return OPEN_STEP;
  const { options, isMultiSelect } = question.content;
  if (isMultiSelect) {
    return {
      id: question.messageId,
      presentation: 'chat',
      mode: 'choose',
      ask: '',
      options,
      isMultiSelect: true,
    };
  }
  return { ...OPEN_STEP, id: question.messageId, options };
};

/** ServerChat wires a chat session the server runs, for the mode and session in the URL. */
export const ServerChat = () => {
  const goBack = useGoBack();
  const { mode = '', chatSessionID = '' } = useParams();
  const { data: configs, isError: hasConfigsFailed } =
    useListConversationConfigsQuery();
  const config = configs?.find((c) => c.mode === mode);

  const chat = useServerChat({ mode: config?.mode, sessionId: chatSessionID });
  // Answered at each pause while recording; the recording is sent as it stops.
  const voiceNote = useVoiceNoteDraft(chat.careCircleId, {
    sendWords: chat.sendVoiceWords,
    attach: chat.attachVoiceRecording,
    deliver: chat.sendVoiceNote,
  });
  const getDocumentUrl = useDocumentUrl(chat.careCircleId);
  const { openExternalUrl } = useOpenUrl();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // What was said is read under each note, as the chat answers it.
  const [isVoiceTextShown, setIsVoiceTextShown] = useState(true);

  // The profile ends the conversation, so a recording still going is stopped, and sent.
  const { onStop } = voiceNote.recorderProps;
  useEffect(() => {
    if (chat.profileId) onStop();
  }, [chat.profileId, onStop]);

  // Only a session of a conversation the server offers is shown.
  const isUnknown =
    hasConfigsFailed || (!!configs && !config) || chat.isSessionMissing;
  if (isUnknown) return <Navigate to={ROUTES.SANDBOX} replace />;
  if (!chat.isSessionReady) return <Loading />;

  const recorderProps: RecorderProps = {
    ...voiceNote.recorderProps,
    onSend: () => voiceNote.send(chat.sendVoiceNote),
  };

  // Waiting on something sent being read, it says so in turn. What a turn
  // does is in the thread, above its typing.
  const threadEnd = chat.isAgentReading && (
    <ReadingLine lines={READING_LINES} />
  );

  const step = questionStep(chat.question);
  // Its options are picked from the question itself, unless several are.
  const openMenuId =
    step.isMultiSelect || !step.options?.length
      ? undefined
      : chat.question?.messageId;

  const handleAttachFile = (source: UploadSource) => {
    const input = source === 'photos' ? photoInputRef : fileInputRef;
    input.current?.click();
  };

  // Sent as soon as they are picked, and read once they are processed.
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    chat.sendFiles(Array.from(input.files ?? []));
    input.value = '';
  };

  const handleOpenDocument = async (documentId: string) => {
    const url = await getDocumentUrl(documentId);
    if (url) openExternalUrl(url);
  };

  return (
    <>
      <ConversationalFormPage
        messages={chat.messages}
        currentStep={step}
        openMenuId={openMenuId}
        isThinking={chat.isAgentThinking}
        threadEnd={threadEnd}
        onBack={goBack}
        onChoose={(option) => chat.answer([option])}
        onChooseMany={chat.answer}
        onSend={(text) => chat.sendText(text)}
        recorder={recorderProps}
        onResend={chat.resendMessage}
        onAttachFile={handleAttachFile}
        onOpenDocument={handleOpenDocument}
        getDocumentUrl={getDocumentUrl}
        voiceControl='button'
        isVoiceTextShown={isVoiceTextShown}
        onToggleVoiceText={() => setIsVoiceTextShown((isShown) => !isShown)}
      />
      <input
        ref={photoInputRef}
        type='file'
        hidden
        multiple
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        onChange={handleFilesSelected}
      />
      <input
        ref={fileInputRef}
        type='file'
        hidden
        multiple
        accept={ACCEPTED_DOCUMENT_TYPES_INPUT}
        onChange={handleFilesSelected}
      />
    </>
  );
};

ServerChat.displayName = 'ServerChat';
export default ServerChat;
